import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronRight, Plus, Play, Pause, Trash2, Mail, Clock, Users,
  BarChart3, Target, Zap, Edit2, Save, X,
  Send, AlertCircle, CheckCircle2, Loader2, ArrowLeft,
  TrendingUp, Shield, Share2, Award, Activity, CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/new-components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/new-components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/new-components/ui/tabs';
import {
  getInitiatives, getInitiative, createInitiative, updateInitiative,
  getCampaigns, getCampaign, createCampaign, updateCampaign,
  createCampaignStep, updateCampaignStep, deleteCampaignStep,
  getOrchestrationStats, getCampaignEvents,
  getKpiDashboard, getReferralLeaderboard,
  getEmailCampaigns, createEmailCampaign, sendEmailCampaign,
  getDeals
} from '../../services/adminDealFlowApi';

// ─── Types ─────────────────────────────────────────────────
interface Initiative {
  id: string; name: string; description: string; status: string;
  is_default: boolean; campaign_count: number;
  total_enrolled: number; active_enrolled: number; completed_enrolled: number;
  created_at: string;
}

interface CampaignStep {
  id: string; step_number: number; delay_hours: number;
  action_type: string; subject: string; body_html: string; body_text: string;
  is_active: boolean;
}

interface Campaign {
  id: string; initiative_id: string; initiative_name: string;
  name: string; description: string; trigger_event: string;
  is_active: boolean; is_default: boolean;
  total_enrolled: number; active_enrolled: number; step_count: number;
  steps?: CampaignStep[];
  enrollments?: any[];
  recent_sends?: any[];
  created_at: string;
}

interface Stats {
  initiatives: { total: number; active: number };
  campaigns: { total: number; active: number };
  enrollments: { total: number; active: number; completed: number };
  sends: { total: number };
  lifecycle_breakdown: { stage: string; count: number }[];
}

interface KPIData {
  funnel: any;
  velocity: any;
  risk: any;
  campaign_performance: any[];
  referrals: any;
}

// ─── Helpers ───────────────────────────────────────────────
const pct = (v: any) => (v !== null && v !== undefined ? `${v}%` : '—');
const hrs = (v: any) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return n < 1 ? '<1h' : n < 24 ? `${Math.round(n)}h` : `${(n / 24).toFixed(1)}d`;
};
const num = (v: any) => (v !== null && v !== undefined ? Number(v).toLocaleString() : '0');

const LIFECYCLE_COLORS: Record<string, string> = {
  lead: 'bg-slate-400', invited: 'bg-blue-500', registered: 'bg-indigo-500',
  verified: 'bg-emerald-500', invested: 'bg-amber-500', repeat_investor: 'bg-purple-500', dormant: 'bg-red-400'
};
const LIFECYCLE_ORDER = ['lead', 'invited', 'registered', 'verified', 'invested', 'repeat_investor', 'dormant'];

const PIPELINE_STAGES = [
  { value: '_all', label: 'All stages' },
  { value: 'prospect', label: 'Prospects' },
  { value: 'interested', label: 'Interested' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'committing', label: 'Committing' },
  { value: 'complete', label: 'Complete' },
];

// ─── Component ─────────────────────────────────────────────
const Campaigns: React.FC = () => {
  const [view, setView] = useState<'overview' | 'initiative' | 'campaign'>('overview');
  const [activeTab, setActiveTab] = useState('orchestration');
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [selectedInitiative, setSelectedInitiative] = useState<any>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [kpi, setKpi] = useState<KPIData | null>(null);
  const [kpiLoaded, setKpiLoaded] = useState(false);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Email blast state (merged from Communications)
  const [emailCampaigns, setEmailCampaigns] = useState<any[]>([]);
  const [deals, setDeals] = useState<any[]>([]);
  const [showCompose, setShowCompose] = useState(false);
  const [sendingEmail, setSendingEmail] = useState<string | null>(null);
  const [scheduleBlastId, setScheduleBlastId] = useState<string | null>(null);
  const [scheduleBlastDate, setScheduleBlastDate] = useState('');
  const [newEmailCampaign, setNewEmailCampaign] = useState({
    name: '', subject: '', body_html: '', deal_id: '_all', pipeline_stage: '_all'
  });

  // Dialog state
  const [showInitDialog, setShowInitDialog] = useState(false);
  const [showCampDialog, setShowCampDialog] = useState(false);
  const [showStepDialog, setShowStepDialog] = useState(false);
  const [editingStep, setEditingStep] = useState<CampaignStep | null>(null);

  // Form state
  const [initForm, setInitForm] = useState({ name: '', description: '' });
  const [campForm, setCampForm] = useState({ initiative_id: '', name: '', description: '', trigger_event: 'lifecycle.lead' });
  const [stepForm, setStepForm] = useState({
    step_number: 1, delay_hours: 0, subject: '', body_html: '', body_text: ''
  });

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [initRes, statsRes, eventsRes, kpiRes, lbRes, emailRes, dealsRes] = await Promise.all([
        getInitiatives().catch(() => ({ data: [] })),
        getOrchestrationStats().catch(() => ({ data: null })),
        getCampaignEvents({ limit: 20 }).catch(() => ({ data: [] })),
        getKpiDashboard().catch(() => ({ data: null })),
        getReferralLeaderboard().catch(() => ({ data: [] })),
        getEmailCampaigns().catch(() => ({ data: [] })),
        getDeals().catch(() => ({ data: [] }))
      ]);
      setInitiatives(initRes.data || []);
      setStats(statsRes.data || null);
      setEvents(eventsRes.data || []);
      setKpi(kpiRes.data || null);
      setKpiLoaded(true);
      setLeaderboard(lbRes.data || []);
      setEmailCampaigns(emailRes.data || []);
      setDeals(dealsRes.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const openInitiative = async (id: string) => {
    setLoading(true);
    try {
      const res = await getInitiative(id);
      setSelectedInitiative(res.data);
      setView('initiative');
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const openCampaign = async (id: string) => {
    setLoading(true);
    try {
      const res = await getCampaign(id);
      setSelectedCampaign(res.data);
      setView('campaign');
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleCreateInitiative = async () => {
    setSaving(true);
    try {
      await createInitiative(initForm);
      setShowInitDialog(false);
      setInitForm({ name: '', description: '' });
      loadOverview();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleCreateCampaign = async () => {
    setSaving(true);
    try {
      await createCampaign(campForm);
      setShowCampDialog(false);
      setCampForm({ initiative_id: '', name: '', description: '', trigger_event: 'lifecycle.lead' });
      if (selectedInitiative) openInitiative(selectedInitiative.id);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleToggleCampaign = async (campaign: Campaign) => {
    try {
      await updateCampaign(campaign.id, { is_active: !campaign.is_active });
      if (selectedInitiative) openInitiative(selectedInitiative.id);
      else loadOverview();
    } catch (err) { console.error(err); }
  };

  const handleSaveStep = async () => {
    if (!selectedCampaign) return;
    setSaving(true);
    try {
      if (editingStep) {
        await updateCampaignStep(selectedCampaign.id, editingStep.id, stepForm);
      } else {
        await createCampaignStep(selectedCampaign.id, stepForm);
      }
      setShowStepDialog(false);
      setEditingStep(null);
      openCampaign(selectedCampaign.id);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleDeleteStep = async (stepId: string) => {
    if (!selectedCampaign || !window.confirm('Delete this step?')) return;
    try {
      await deleteCampaignStep(selectedCampaign.id, stepId);
      openCampaign(selectedCampaign.id);
    } catch (err) { console.error(err); }
  };

  // Email blast handlers (from Communications)
  const handleCreateEmailCampaign = async () => {
    try {
      await createEmailCampaign({
        ...newEmailCampaign,
        deal_id: newEmailCampaign.deal_id === '_all' ? undefined : newEmailCampaign.deal_id,
        pipeline_stage: newEmailCampaign.pipeline_stage === '_all' ? undefined : newEmailCampaign.pipeline_stage,
      });
      setShowCompose(false);
      setNewEmailCampaign({ name: '', subject: '', body_html: '', deal_id: '_all', pipeline_stage: '_all' });
      loadOverview();
    } catch (err) { console.error(err); }
  };

  const handleSendEmail = async (id: string) => {
    try {
      setSendingEmail(id);
      await sendEmailCampaign(id);
      loadOverview();
    } catch (err) { console.error(err); }
    finally { setSendingEmail(null); }
  };

  const handleScheduleBlast = async () => {
    if (!scheduleBlastId || !scheduleBlastDate) return;
    try {
      setSendingEmail(scheduleBlastId);
      await sendEmailCampaign(scheduleBlastId, { scheduled_at: new Date(scheduleBlastDate).toISOString() });
      setScheduleBlastId(null);
      setScheduleBlastDate('');
      loadOverview();
    } catch (err) { console.error(err); }
    finally { setSendingEmail(null); }
  };

  const goBack = () => {
    if (view === 'campaign') {
      if (selectedCampaign && selectedInitiative) openInitiative(selectedInitiative.id);
      else { setView('overview'); loadOverview(); }
    } else {
      setView('overview');
      loadOverview();
    }
  };

  if (loading && view === 'overview' && initiatives.length === 0 && emailCampaigns.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // CAMPAIGN DETAIL VIEW
  // ═══════════════════════════════════════════════════════════
  if (view === 'campaign' && selectedCampaign) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{selectedCampaign.name}</h1>
            <p className="text-sm text-muted-foreground">{selectedCampaign.description}</p>
          </div>
          <Badge variant={selectedCampaign.is_active ? 'default' : 'secondary'}>
            {selectedCampaign.is_active ? 'Active' : 'Paused'}
          </Badge>
          <Button variant="outline" size="sm" onClick={() => handleToggleCampaign(selectedCampaign)}>
            {selectedCampaign.is_active ? <><Pause className="h-4 w-4 mr-1" /> Pause</> : <><Play className="h-4 w-4 mr-1" /> Activate</>}
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="pt-4">
            <div className="text-2xl font-bold">{selectedCampaign.trigger_event}</div>
            <div className="text-xs text-muted-foreground">Trigger Event</div>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <div className="text-2xl font-bold">{selectedCampaign.steps?.length || 0}</div>
            <div className="text-xs text-muted-foreground">Steps</div>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <div className="text-2xl font-bold">{selectedCampaign.active_enrolled || 0}</div>
            <div className="text-xs text-muted-foreground">Active Enrollments</div>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <div className="text-2xl font-bold">{selectedCampaign.recent_sends?.length || 0}</div>
            <div className="text-xs text-muted-foreground">Recent Sends</div>
          </CardContent></Card>
        </div>

        {/* Steps */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Campaign Steps</CardTitle>
            <Button size="sm" onClick={() => {
              setEditingStep(null);
              setStepForm({ step_number: (selectedCampaign.steps?.length || 0) + 1, delay_hours: 24, subject: '', body_html: '', body_text: '' });
              setShowStepDialog(true);
            }}>
              <Plus className="h-4 w-4 mr-1" /> Add Step
            </Button>
          </CardHeader>
          <CardContent>
            {selectedCampaign.steps && selectedCampaign.steps.length > 0 ? (
              <div className="space-y-3">
                {selectedCampaign.steps.map((step, idx) => (
                  <div key={step.id} className="flex items-start gap-4 p-4 border rounded-lg">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                        {step.step_number}
                      </div>
                      {idx < (selectedCampaign.steps?.length || 0) - 1 && <div className="w-px h-6 bg-border mt-1" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm truncate">{step.subject}</span>
                        {!step.is_active && <Badge variant="secondary" className="text-xs">Disabled</Badge>}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {step.delay_hours === 0 ? 'Immediately' : step.delay_hours < 24 ? `${step.delay_hours}h delay` : `${Math.round(step.delay_hours / 24)}d delay`}
                        </span>
                        <span>{step.action_type}</span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => {
                        setEditingStep(step);
                        setStepForm({ step_number: step.step_number, delay_hours: step.delay_hours, subject: step.subject, body_html: step.body_html || '', body_text: step.body_text || '' });
                        setShowStepDialog(true);
                      }}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteStep(step.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No steps configured. Add your first step to start the sequence.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Enrollments */}
        {selectedCampaign.enrollments && selectedCampaign.enrollments.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Active Enrollments</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm">
                  <thead><tr className="border-b text-left">
                    <th className="pb-2">Contact</th><th className="pb-2">Stage</th>
                    <th className="pb-2">Current Step</th><th className="pb-2">Status</th><th className="pb-2">Enrolled</th>
                  </tr></thead>
                  <tbody>
                    {selectedCampaign.enrollments.map((e: any) => (
                      <tr key={e.id} className="border-b last:border-0">
                        <td className="py-2">{e.first_name} {e.last_name} <span className="text-muted-foreground">({e.email})</span></td>
                        <td><Badge variant="outline" className="text-xs">{e.lifecycle_stage}</Badge></td>
                        <td>{e.current_step}</td>
                        <td><Badge variant={e.status === 'active' ? 'default' : 'secondary'} className="text-xs">{e.status}</Badge></td>
                        <td className="text-muted-foreground">{new Date(e.enrolled_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step Dialog */}
        <Dialog open={showStepDialog} onOpenChange={setShowStepDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editingStep ? 'Edit Step' : 'Add Step'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Step Number</label>
                  <Input type="number" min={1} value={stepForm.step_number} onChange={e => setStepForm(f => ({ ...f, step_number: Number(e.target.value) }))} />
                </div>
                <div>
                  <label className="text-sm font-medium">Delay (hours)</label>
                  <Input type="number" min={0} value={stepForm.delay_hours} onChange={e => setStepForm(f => ({ ...f, delay_hours: Number(e.target.value) }))} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Subject Line</label>
                <Input value={stepForm.subject} onChange={e => setStepForm(f => ({ ...f, subject: e.target.value }))} placeholder="Welcome to {{first_name}}..." />
              </div>
              <div>
                <label className="text-sm font-medium">Email Body (HTML)</label>
                <textarea className="w-full h-48 border rounded-md p-3 text-sm font-mono" value={stepForm.body_html}
                  onChange={e => setStepForm(f => ({ ...f, body_html: e.target.value }))} placeholder="<h2>Hi {{first_name}},</h2><p>...</p>" />
              </div>
              <div>
                <label className="text-sm font-medium">Plain Text (optional)</label>
                <textarea className="w-full h-24 border rounded-md p-3 text-sm" value={stepForm.body_text}
                  onChange={e => setStepForm(f => ({ ...f, body_text: e.target.value }))} placeholder="Plain text fallback..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowStepDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveStep} disabled={saving || !stepForm.subject}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                {editingStep ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // INITIATIVE DETAIL VIEW
  // ═══════════════════════════════════════════════════════════
  if (view === 'initiative' && selectedInitiative) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBack}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{selectedInitiative.name}</h1>
            <p className="text-sm text-muted-foreground">{selectedInitiative.description}</p>
          </div>
          <Badge variant={selectedInitiative.status === 'active' ? 'default' : 'secondary'}>{selectedInitiative.status}</Badge>
        </div>

        <Button size="sm" onClick={() => {
          setCampForm({ initiative_id: selectedInitiative.id, name: '', description: '', trigger_event: 'lifecycle.lead' });
          setShowCampDialog(true);
        }}>
          <Plus className="h-4 w-4 mr-1" /> Add Campaign
        </Button>

        <div className="space-y-3">
          {selectedInitiative.campaigns?.map((camp: Campaign) => (
            <Card key={camp.id} className="cursor-pointer hover:border-primary transition-colors" onClick={() => openCampaign(camp.id)}>
              <CardContent className="flex items-center gap-4 py-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${camp.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {camp.is_active ? <Zap className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{camp.name}</div>
                  <div className="text-sm text-muted-foreground">{camp.description}</div>
                  <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                    <span>Trigger: {camp.trigger_event}</span>
                    <span>{camp.step_count} steps</span>
                    <span>{camp.total_enrolled || 0} enrolled</span>
                    <span>{camp.active_enrolled || 0} active</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleToggleCampaign(camp); }}>
                    {camp.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </Button>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
          {(!selectedInitiative.campaigns || selectedInitiative.campaigns.length === 0) && (
            <div className="text-center py-12 text-muted-foreground">No campaigns yet. Create one to start orchestrating.</div>
          )}
        </div>

        {/* Create Campaign Dialog */}
        <Dialog open={showCampDialog} onOpenChange={setShowCampDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Campaign</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><label className="text-sm font-medium">Name</label>
                <Input value={campForm.name} onChange={e => setCampForm(f => ({ ...f, name: e.target.value }))} placeholder="Lead Nurture Sequence" /></div>
              <div><label className="text-sm font-medium">Description</label>
                <Input value={campForm.description} onChange={e => setCampForm(f => ({ ...f, description: e.target.value }))} placeholder="Warms leads into registrations" /></div>
              <div><label className="text-sm font-medium">Trigger Event</label>
                <Select value={campForm.trigger_event} onValueChange={v => setCampForm(f => ({ ...f, trigger_event: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lifecycle.lead">Lifecycle: Lead</SelectItem>
                    <SelectItem value="lifecycle.invited">Lifecycle: Invited</SelectItem>
                    <SelectItem value="lifecycle.registered">Lifecycle: Registered</SelectItem>
                    <SelectItem value="lifecycle.verified">Lifecycle: Verified</SelectItem>
                    <SelectItem value="lifecycle.invested">Lifecycle: Invested</SelectItem>
                    <SelectItem value="manual">Manual Enrollment</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCampDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateCampaign} disabled={saving || !campForm.name}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // OVERVIEW — Tabbed: Orchestration | KPI Dashboard | Email Blasts | Referrals
  // ═══════════════════════════════════════════════════════════
  const funnelTotal = kpi?.funnel
    ? Number(kpi.funnel.leads || 0) + Number(kpi.funnel.invited || 0) + Number(kpi.funnel.registered || 0) +
      Number(kpi.funnel.verified || 0) + Number(kpi.funnel.invested || 0) + Number(kpi.funnel.repeat_investors || 0) + Number(kpi.funnel.dormant || 0)
    : 0;

  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold">Campaigns &amp; Communications</h1>
          <p className="text-sm text-muted-foreground">Lifecycle automation, email blasts, conversion KPIs, and referral multipliers</p>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
          <Button variant="outline" onClick={() => { setActiveTab('email'); setShowCompose(true); }} className="justify-center">
            <Mail className="h-4 w-4 mr-2" /> New Email Blast
          </Button>
          <Button onClick={() => { setInitForm({ name: '', description: '' }); setShowInitDialog(true); }} className="justify-center">
            <Plus className="h-4 w-4 mr-2" /> New Initiative
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="orchestration"><Zap className="h-4 w-4 mr-1" /> Orchestration</TabsTrigger>
          <TabsTrigger value="email"><Mail className="h-4 w-4 mr-1" /> Email Blasts</TabsTrigger>
          <TabsTrigger value="kpi"><BarChart3 className="h-4 w-4 mr-1" /> KPI Dashboard</TabsTrigger>
          <TabsTrigger value="referrals"><Share2 className="h-4 w-4 mr-1" /> Referrals</TabsTrigger>
        </TabsList>

        {/* ─── ORCHESTRATION TAB ──────────────────────────── */}
        <TabsContent value="orchestration" className="space-y-6 mt-4">
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
              <Card><CardContent className="pt-4">
                <div className="flex items-center gap-2"><Target className="h-4 w-4 text-primary" /><span className="text-xs text-muted-foreground">Initiatives</span></div>
                <div className="text-2xl font-bold mt-1">{stats.initiatives.active}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" /><span className="text-xs text-muted-foreground">Campaigns</span></div>
                <div className="text-2xl font-bold mt-1">{stats.campaigns.active}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="flex items-center gap-2"><Users className="h-4 w-4 text-blue-500" /><span className="text-xs text-muted-foreground">Active Enrollments</span></div>
                <div className="text-2xl font-bold mt-1">{stats.enrollments.active}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /><span className="text-xs text-muted-foreground">Completed</span></div>
                <div className="text-2xl font-bold mt-1">{stats.enrollments.completed}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="flex items-center gap-2"><Send className="h-4 w-4 text-indigo-500" /><span className="text-xs text-muted-foreground">Total Sends</span></div>
                <div className="text-2xl font-bold mt-1">{stats.sends.total}</div>
              </CardContent></Card>
            </div>
          )}

          {/* Lifecycle Funnel */}
          {stats && stats.lifecycle_breakdown && stats.lifecycle_breakdown.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Contact Lifecycle Distribution</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {LIFECYCLE_ORDER.map(stage => {
                    const entry = stats.lifecycle_breakdown.find(s => s.stage === stage);
                    const count = entry?.count || 0;
                    const total = stats.lifecycle_breakdown.reduce((s, e) => s + e.count, 0);
                    const w = total > 0 ? Math.max((count / total) * 100, count > 0 ? 2 : 0) : 0;
                    return (
                      <div key={stage} className="flex items-center gap-3">
                        <span className="text-xs w-28 capitalize text-muted-foreground">{stage.replace('_', ' ')}</span>
                        <div className="flex-1 bg-muted rounded-full h-5 overflow-hidden">
                          <div className={`h-full rounded-full ${LIFECYCLE_COLORS[stage] || 'bg-gray-400'} transition-all`} style={{ width: `${w}%` }} />
                        </div>
                        <span className="text-sm font-medium w-10 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Initiatives List */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Initiatives</CardTitle></CardHeader>
            <CardContent>
              {initiatives.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Target className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>No initiatives yet. Create one to start orchestrating campaigns.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {initiatives.map(init => (
                    <div key={init.id} className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 border rounded-lg cursor-pointer hover:border-primary transition-colors" onClick={() => openInitiative(init.id)}>
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center"><Target className="h-5 w-5 text-primary" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{init.name}</div>
                        <div className="text-sm text-muted-foreground">{init.description}</div>
                        <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                          <span>{init.campaign_count} campaigns</span>
                          <span>{init.total_enrolled} enrolled</span>
                          <span>{init.active_enrolled} active</span>
                          <span>{init.completed_enrolled} completed</span>
                        </div>
                      </div>
                      <Badge variant={init.status === 'active' ? 'default' : 'secondary'}>{init.status}</Badge>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Events */}
          {events.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Recent Activity</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {events.slice(0, 10).map((ev: any) => (
                    <div key={ev.id} className="flex items-center gap-3 text-sm">
                      <div className={`w-2 h-2 rounded-full ${ev.event_type === 'enrolled' ? 'bg-blue-500' : ev.event_type === 'completed' ? 'bg-green-500' : ev.event_type === 'unenrolled' ? 'bg-red-500' : 'bg-gray-400'}`} />
                      <span className="text-muted-foreground w-32 shrink-0">
                        {new Date(ev.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span className="font-medium">{ev.first_name} {ev.last_name}</span>
                      <Badge variant="outline" className="text-xs">{ev.event_type}</Badge>
                      {ev.campaign_name && <span className="text-muted-foreground">{ev.campaign_name}</span>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ─── EMAIL BLASTS TAB (merged from Communications) ── */}
        <TabsContent value="email" className="space-y-6 mt-4">
          {/* Email Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card><CardContent className="pt-4">
              <div className="text-xs text-muted-foreground">Total Campaigns</div>
              <div className="text-2xl font-bold mt-1">{emailCampaigns.length}</div>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <div className="text-xs text-muted-foreground">Sent</div>
              <div className="text-2xl font-bold text-green-600 mt-1">{emailCampaigns.filter((c: any) => c.status === 'sent').length}</div>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <div className="text-xs text-muted-foreground">Drafts</div>
              <div className="text-2xl font-bold text-amber-600 mt-1">{emailCampaigns.filter((c: any) => c.status === 'draft').length}</div>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <div className="text-xs text-muted-foreground">Total Recipients</div>
              <div className="text-2xl font-bold mt-1">{emailCampaigns.reduce((sum: number, c: any) => sum + (c.recipient_count || 0), 0)}</div>
            </CardContent></Card>
          </div>

          {/* Email Campaigns Table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">Email Blasts</CardTitle>
              <Button size="sm" onClick={() => setShowCompose(true)}>
                <Plus className="h-4 w-4 mr-1" /> New Email Blast
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[920px] text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-4 text-xs text-muted-foreground">Campaign</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Subject</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Deal</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Stage Filter</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Status</th>
                      <th className="text-right p-4 text-xs text-muted-foreground">Recipients</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Created</th>
                      <th className="p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailCampaigns.map((c: any) => (
                      <tr key={c.id} className="border-b last:border-0 hover:bg-muted/50">
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{c.name}</span>
                          </div>
                        </td>
                        <td className="p-4 text-muted-foreground max-w-[200px] truncate">{c.subject}</td>
                        <td className="p-4 text-muted-foreground">{c.deal_title || '—'}</td>
                        <td className="p-4">
                          {c.pipeline_stage ? (
                            <Badge variant="secondary" className="text-xs capitalize">{c.pipeline_stage}</Badge>
                          ) : <span className="text-xs text-muted-foreground">All</span>}
                        </td>
                        <td className="p-4">
                          <Badge className={`text-xs ${
                            c.status === 'sent' ? 'bg-green-50 text-green-700' :
                            c.status === 'sending' ? 'bg-blue-50 text-blue-700' :
                            c.status === 'scheduled' ? 'bg-purple-50 text-purple-700' : ''
                          }`} variant="secondary">
                            {c.status === 'sent' && <CheckCircle className="h-3 w-3 mr-1" />}
                            {c.status === 'sending' && <Clock className="h-3 w-3 mr-1" />}
                            {c.status === 'scheduled' && <Clock className="h-3 w-3 mr-1" />}
                            {c.status}{c.status === 'scheduled' && c.scheduled_at ? ` ${new Date(c.scheduled_at).toLocaleString()}` : ''}
                          </Badge>
                        </td>
                        <td className="p-4 text-right">{c.recipient_count || 0}</td>
                        <td className="p-4 text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</td>
                        <td className="p-4 text-right">
                          {c.status === 'draft' && (
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" onClick={() => handleSendEmail(c.id)} disabled={sendingEmail === c.id}>
                                {sendingEmail === c.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <><Send className="h-3 w-3 mr-1" /> Send</>
                                )}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => { setScheduleBlastId(c.id); setScheduleBlastDate(''); }}>
                                <Clock className="h-3 w-3 mr-1" /> Schedule
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {emailCampaigns.length === 0 && (
                      <tr><td colSpan={8} className="p-12 text-center text-muted-foreground">
                        <Mail className="h-8 w-8 mx-auto mb-2 opacity-40" />
                        No email blasts yet. Create one to reach your pipeline.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── KPI DASHBOARD TAB ─────────────────────────── */}
        <TabsContent value="kpi" className="space-y-6 mt-4">
          {!kpiLoaded ? (
            <div className="text-center py-12 text-muted-foreground">
              <Loader2 className="h-10 w-10 mx-auto mb-3 animate-spin opacity-40" />
              <p>Loading KPI data...</p>
            </div>
          ) : !kpi ? (
            <div className="text-center py-12 text-muted-foreground">
              <BarChart3 className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>KPI data not available. Ensure the database views are created.</p>
            </div>
          ) : (
            <>
              {/* Funnel Conversion Rates */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-blue-500" /><span className="text-xs text-muted-foreground">Registration Rate</span></div>
                    <div className="text-2xl font-bold mt-1">{pct(kpi.funnel?.registration_rate)}</div>
                    <div className="text-xs text-muted-foreground">Contact → Registered</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-green-500" /><span className="text-xs text-muted-foreground">KYC Rate</span></div>
                    <div className="text-2xl font-bold mt-1">{pct(kpi.funnel?.kyc_rate)}</div>
                    <div className="text-xs text-muted-foreground">Registered → Verified</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" /><span className="text-xs text-muted-foreground">Activation Rate</span></div>
                    <div className="text-2xl font-bold mt-1">{pct(kpi.funnel?.activation_rate)}</div>
                    <div className="text-xs text-muted-foreground">Verified → Invested</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2"><Award className="h-4 w-4 text-purple-500" /><span className="text-xs text-muted-foreground">Repeat Rate</span></div>
                    <div className="text-2xl font-bold mt-1">{pct(kpi.funnel?.repeat_rate)}</div>
                    <div className="text-xs text-muted-foreground">First → Repeat</div>
                  </CardContent>
                </Card>
              </div>

              {/* Funnel Visualization */}
              {funnelTotal > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Capital Conversion Funnel</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {[
                        { label: 'Leads', value: kpi.funnel?.leads, color: 'bg-slate-400' },
                        { label: 'Invited', value: kpi.funnel?.invited, color: 'bg-blue-500' },
                        { label: 'Registered', value: kpi.funnel?.registered, color: 'bg-indigo-500' },
                        { label: 'Verified', value: kpi.funnel?.verified, color: 'bg-emerald-500' },
                        { label: 'Invested', value: kpi.funnel?.invested, color: 'bg-amber-500' },
                        { label: 'Repeat Investors', value: kpi.funnel?.repeat_investors, color: 'bg-purple-500' },
                      ].map((s) => {
                        const v = Number(s.value || 0);
                        const w = funnelTotal > 0 ? Math.max((v / funnelTotal) * 100, v > 0 ? 3 : 0) : 0;
                        return (
                          <div key={s.label} className="flex items-center gap-3">
                            <span className="text-xs w-32 text-muted-foreground">{s.label}</span>
                            <div className="flex-1 flex justify-center">
                              <div className={`${s.color} rounded h-7 transition-all flex items-center justify-center`} style={{ width: `${w}%`, minWidth: v > 0 ? '32px' : '0' }}>
                                <span className="text-xs font-bold text-white">{v > 0 ? v : ''}</span>
                              </div>
                            </div>
                            <span className="text-xs font-medium w-8 text-right">{num(v)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Velocity + Risk */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4" /> Velocity Metrics</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between"><span className="text-sm text-muted-foreground">Time to Registration</span><span className="font-medium">{hrs(kpi.velocity?.median_hours_to_registration)}</span></div>
                      <div className="flex justify-between"><span className="text-sm text-muted-foreground">Time to Verification</span><span className="font-medium">{hrs(kpi.velocity?.median_hours_to_verification)}</span></div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Shield className="h-4 w-4" /> Risk Overlay</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between"><span className="text-sm text-muted-foreground">Compliance Holds</span><span className="font-medium">{num(kpi.risk?.holds_count)} <span className="text-muted-foreground">({pct(kpi.risk?.hold_rate)})</span></span></div>
                      <div className="flex justify-between"><span className="text-sm text-muted-foreground">Unsubscribed</span><span className="font-medium">{num(kpi.risk?.unsubscribed_count)} <span className="text-muted-foreground">({pct(kpi.risk?.unsubscribe_rate)})</span></span></div>
                      <div className="flex justify-between"><span className="text-sm text-muted-foreground">Total Contacts</span><span className="font-medium">{num(kpi.risk?.total_contacts)}</span></div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Campaign Performance Table */}
              {kpi.campaign_performance && kpi.campaign_performance.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Campaign Performance</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[760px] text-sm">
                        <thead>
                          <tr className="border-b text-left bg-muted/50">
                            <th className="p-3">Campaign</th><th className="p-3 text-right">Sent</th>
                            <th className="p-3 text-right">Opened</th><th className="p-3 text-right">Clicked</th>
                            <th className="p-3 text-right">Open Rate</th><th className="p-3 text-right">Click Rate</th>
                            <th className="p-3 text-right">Failed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kpi.campaign_performance.map((cp: any) => (
                            <tr key={cp.campaign_id} className="border-b last:border-0 hover:bg-muted/30">
                              <td className="p-3 font-medium">{cp.campaign_name}</td>
                              <td className="p-3 text-right">{num(cp.sent)}</td>
                              <td className="p-3 text-right">{num(cp.opened)}</td>
                              <td className="p-3 text-right">{num(cp.clicked)}</td>
                              <td className="p-3 text-right">{pct(cp.open_rate)}</td>
                              <td className="p-3 text-right">{pct(cp.click_rate)}</td>
                              <td className="p-3 text-right text-destructive">{num(cp.failed)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* ─── REFERRALS TAB ─────────────────────────────── */}
        <TabsContent value="referrals" className="space-y-6 mt-4">
          {/* Referral Metrics */}
          {kpi?.referrals && Object.keys(kpi.referrals).length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
              <Card><CardContent className="pt-4">
                <div className="text-xs text-muted-foreground">Total Referrals</div>
                <div className="text-2xl font-bold">{num(kpi.referrals.total_referrals)}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="text-xs text-muted-foreground">Unique Referrers</div>
                <div className="text-2xl font-bold">{num(kpi.referrals.unique_referrers)}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="text-xs text-muted-foreground">Registered</div>
                <div className="text-2xl font-bold">{num(kpi.referrals.registered)}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="text-xs text-muted-foreground">Invested</div>
                <div className="text-2xl font-bold">{num(kpi.referrals.invested)}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="text-xs text-muted-foreground">Conversion Rate</div>
                <div className="text-2xl font-bold">{pct(kpi.referrals.conversion_rate)}</div>
              </CardContent></Card>
            </div>
          )}

          {/* Leaderboard */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Award className="h-4 w-4" /> Top Referrers</CardTitle></CardHeader>
            <CardContent className="p-0">
              {leaderboard.length > 0 ? (
                <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <thead>
                    <tr className="border-b text-left bg-muted/50">
                      <th className="p-3">#</th><th className="p-3">User</th><th className="p-3">Code</th>
                      <th className="p-3 text-right">Total</th><th className="p-3 text-right">Registered</th>
                      <th className="p-3 text-right">Verified</th><th className="p-3 text-right">Invested</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map((r: any, i: number) => (
                      <tr key={r.referrer_user_id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="p-3 font-bold text-muted-foreground">{i + 1}</td>
                        <td className="p-3"><div className="font-medium">{r.first_name} {r.last_name}</div><div className="text-xs text-muted-foreground">{r.email}</div></td>
                        <td className="p-3"><Badge variant="outline" className="font-mono text-xs">{r.referral_code}</Badge></td>
                        <td className="p-3 text-right font-medium">{r.total_referrals}</td>
                        <td className="p-3 text-right">{r.registered}</td>
                        <td className="p-3 text-right">{r.verified}</td>
                        <td className="p-3 text-right text-amber-600 font-medium">{r.invested}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Share2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>No referrals yet. Referral links are generated when invested users share their code.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Initiative Dialog */}
      <Dialog open={showInitDialog} onOpenChange={setShowInitDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Initiative</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium">Name</label>
              <Input value={initForm.name} onChange={e => setInitForm(f => ({ ...f, name: e.target.value }))} placeholder="New User Onboarding" /></div>
            <div><label className="text-sm font-medium">Description</label>
              <Input value={initForm.description} onChange={e => setInitForm(f => ({ ...f, description: e.target.value }))} placeholder="Convert leads through the full lifecycle funnel" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInitDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateInitiative} disabled={saving || !initForm.name}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compose Email Blast Dialog */}
      <Dialog open={showCompose} onOpenChange={setShowCompose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New Email Blast</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Campaign Name</label>
              <Input value={newEmailCampaign.name} onChange={e => setNewEmailCampaign(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Q2 Pipeline Update" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Target Deal (optional)</label>
                <Select value={newEmailCampaign.deal_id} onValueChange={v => setNewEmailCampaign(p => ({ ...p, deal_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="All deals" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All deals</SelectItem>
                    {deals.map((d: any) => (
                      <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Pipeline Stage Filter</label>
                <Select value={newEmailCampaign.pipeline_stage} onValueChange={v => setNewEmailCampaign(p => ({ ...p, pipeline_stage: v }))}>
                  <SelectTrigger><SelectValue placeholder="All stages" /></SelectTrigger>
                  <SelectContent>
                    {PIPELINE_STAGES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Subject</label>
              <Input value={newEmailCampaign.subject} onChange={e => setNewEmailCampaign(p => ({ ...p, subject: e.target.value }))} placeholder="Email subject line" />
            </div>
            <div>
              <label className="text-sm font-medium">Body (HTML)</label>
              <textarea
                value={newEmailCampaign.body_html}
                onChange={e => setNewEmailCampaign(p => ({ ...p, body_html: e.target.value }))}
                rows={8}
                className="w-full rounded-md border px-3 py-2 text-sm font-mono"
                placeholder={'<p>Hello {{name}},</p>\n\n<p>We\'re excited to announce...</p>'}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Use {'{{name}}'} for recipient's name. Will be wrapped in branded email template.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompose(false)}>Cancel</Button>
            <Button onClick={handleCreateEmailCampaign} disabled={!newEmailCampaign.name || !newEmailCampaign.subject || !newEmailCampaign.body_html}>
              Create Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Blast Dialog */}
      <Dialog open={!!scheduleBlastId} onOpenChange={(o) => { if (!o) setScheduleBlastId(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Schedule Email Blast</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <label className="text-sm font-medium block">Send Date & Time</label>
            <Input
              type="datetime-local"
              value={scheduleBlastDate}
              onChange={(e) => setScheduleBlastDate(e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleBlastId(null)}>Cancel</Button>
            <Button onClick={handleScheduleBlast} disabled={!scheduleBlastDate || !!sendingEmail}>
              {sendingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Clock className="h-3 w-3 mr-1" /> Schedule</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Campaigns;
